import { create } from '@bufbuild/protobuf';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  type AccessRule,
  AccessRuleSchema,
  type CollectionAccessRule,
  CollectionAccessRuleAccessType,
  type DatabaseApiKey,
  GlobalAccessRuleAccessType,
} from '@qdrant/qdrant-cloud-public-api/cluster/auth/v2';
import { type ClusterValid } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { z } from 'zod';
import {
  accessControlSchema,
  collectionOperationsSchema,
  refineAccessControl,
} from '@/components/Common/ApiKeys/api-key-form';
import {
  type ApiKeyResource,
  type ApiKeyView,
  type ApiKeyViewAccess,
  type ApiKeyViewStatus,
  EXPIRED_STATUS_LABEL,
} from '@/components/Common/ApiKeys/api-key-view';
import { formatKeyCreator } from '@/utils/api-key-creator';
import { getClusterEndpoint, hasClusterRbacEnabled } from '@/utils/cluster-utils';
import { timestampToDate } from '@/utils/date-utils';
import { expirationDaysValidator, validators } from '@/utils/form-utils';
import { keyValuePairArrayValidator } from '@/utils/kubernetes-utils';
import { UNREACHABLE_CLUSTER_MSG } from './collections-context';

export const MIN_CLUSTER_VERSION_FOR_RBAC = '1.11.0';

type CollectionAccess = {
  $typeName: 'qdrant.cloud.cluster.auth.v2.AccessRule';
  scope: {
    value: CollectionAccessRule;
    case: 'collectionAccess';
  };
};

const hasCollectionAccess = (accessRules: AccessRule[]): accessRules is CollectionAccess[] => {
  for (const rule of accessRules) {
    if (rule.scope.case === 'collectionAccess') {
      return true;
    }
  }
  return false;
};

const GLOBAL_ACCESS_TYPE_STRINGS_MAP: Record<GlobalAccessRuleAccessType, string> = {
  [GlobalAccessRuleAccessType.READ_ONLY]: 'Read',
  [GlobalAccessRuleAccessType.MANAGE]: 'Manage',
  [GlobalAccessRuleAccessType.UNSPECIFIED]: 'Unspecified',
} as const;

const COLLECTION_ACCESS_TYPE_STRINGS_MAP: Record<CollectionAccessRuleAccessType, string> = {
  [CollectionAccessRuleAccessType.READ_ONLY]: 'Read',
  [CollectionAccessRuleAccessType.READ_WRITE]: 'Read/Write',
  [CollectionAccessRuleAccessType.UNSPECIFIED]: 'Unspecified',
} as const;

function getCollectionAccessDataName(accessRule: CollectionAccess): string {
  return COLLECTION_ACCESS_TYPE_STRINGS_MAP[accessRule.scope.value.accessType]
    .toUpperCase()
    .concat(` ${accessRule.scope.value.collectionName}`);
}

function getGlobalAccessType(accessRule: AccessRule): GlobalAccessRuleAccessType | null {
  if (accessRule.scope.case === 'globalAccess' && accessRule.scope.value) {
    // Type assertion is safe here because we've checked the case
    const globalValue = accessRule.scope.value as { accessType: GlobalAccessRuleAccessType };
    return globalValue.accessType;
  }
  return null;
}

function getFirstGlobalAccessType(accessRules: AccessRule[]): GlobalAccessRuleAccessType | null {
  for (const rule of accessRules) {
    const globalType = getGlobalAccessType(rule);
    if (globalType) {
      return globalType;
    }
  }
  return null;
}

const getTokenStatus = ({ expiresAt }: DatabaseApiKey): ApiKeyViewStatus => {
  if (expiresAt && timestampDate(expiresAt).getTime() < Date.now()) {
    return { label: EXPIRED_STATUS_LABEL, color: 'error' };
  }
  return { label: 'Active', color: 'success' };
};

const getTokenAccess = (accessRules: AccessRule[]): ApiKeyViewAccess | null => {
  if (!accessRules.length) {
    return null;
  }
  if (hasCollectionAccess(accessRules)) {
    return { scope: 'collections', labels: accessRules.map(getCollectionAccessDataName) };
  }
  const accessType = getFirstGlobalAccessType(accessRules) ?? GlobalAccessRuleAccessType.UNSPECIFIED;
  return { scope: 'global', label: `${GLOBAL_ACCESS_TYPE_STRINGS_MAP[accessType]} Cluster` };
};

export const getClusterApiKeyView = (dbApiKey: DatabaseApiKey): ApiKeyView => ({
  access: getTokenAccess(dbApiKey.accessRules),
  createdAt: timestampToDate(dbApiKey.createdAt),
  createdBy: formatKeyCreator(dbApiKey.createdByEmail, dbApiKey.createdByActorType),
  deletable: true,
  expiresAt: timestampToDate(dbApiKey.expiresAt),
  id: dbApiKey.id,
  name: dbApiKey.name,
  postfix: dbApiKey.postfix,
  status: getTokenStatus(dbApiKey),
});

export const getClusterApiKeyResource = (cluster: ClusterValid): ApiKeyResource => ({
  kind: 'cluster',
  name: cluster.name,
  endpointUrl: getClusterEndpoint(cluster)?.url,
  // Custom-permission (RBAC) keys can only be created when the cluster has JWT/RBAC enabled.
  // Legacy clusters go through the v1 single-key flow and have no such option, so don't imply it.
  canCreateCustomPermissionKeys: hasClusterRbacEnabled(cluster),
});

export const collectionsSchemaValidator = z.object({
  name: z.string().min(1),
  allowedOperations: collectionOperationsSchema,
  payload: keyValuePairArrayValidator.optional(),
});
export type CollectionsSchema = z.infer<typeof collectionsSchemaValidator>;

export const tokenSchemaValidator = z
  .object({
    // Token name follows the same restrictions as the Cluster name.
    name: validators.clusterName,
    expiration: expirationDaysValidator,
    accessControl: accessControlSchema,
    allowedOperations: z.enum(['m', 'r']).optional(),
    collections: z.array(collectionsSchemaValidator).optional(),
  })
  .superRefine(refineAccessControl);

export type TokenSchemaType = z.infer<typeof tokenSchemaValidator>;

export const getDbApiKeyAccessRules = (data: TokenSchemaType): AccessRule[] => {
  const accessRules: AccessRule[] = [];

  if (data.accessControl === 'globalAccess') {
    accessRules.push(
      create(AccessRuleSchema, {
        scope: {
          case: 'globalAccess',
          value: {
            accessType:
              data.allowedOperations === 'm' ? GlobalAccessRuleAccessType.MANAGE : GlobalAccessRuleAccessType.READ_ONLY,
          },
        },
      }),
    );
  } else {
    data.collections?.forEach((collection) =>
      accessRules.push(
        create(AccessRuleSchema, {
          scope: {
            case: 'collectionAccess',
            value: {
              accessType:
                collection.allowedOperations === 'rw'
                  ? CollectionAccessRuleAccessType.READ_WRITE
                  : CollectionAccessRuleAccessType.READ_ONLY,
              collectionName: collection.name,
            },
          },
        }),
      ),
    );
  }

  return accessRules;
};

export const getUnavailableCollectionsReason = (hasError: boolean, clusterIsNotReachable: boolean): string => {
  if (hasError) {
    return 'There was a problem retrieving the collections.';
  } else if (clusterIsNotReachable) {
    return UNREACHABLE_CLUSTER_MSG;
  }
  return 'No collections are available.';
};
